import { serializeError } from "serialize-error";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  type ChatMessage,
  type ChatParameters,
  type Database,
  supabase,
  supabaseBaseQuery,
  type Chat,
} from "../../../supabase";
import type { CreateChatPayload } from "../types";
import { resourcesApi } from "../../resources/api/resourcesApi";

const chatsApi = createApi({
  reducerPath: "api.Chats",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["Chat"],
  endpoints: (build) => ({
    createChat: build.mutation<NonNullable<Chat["id"]>, CreateChatPayload>({
      queryFn: async (parameters) => {
        const { name, parent_id, icon, meta } = parameters;
        const { data, error } = await supabase.rpc("create_chat", {
          p_name: name,
          p_parent_id: parent_id,
          p_icon: icon,
          p_meta: meta as Database["public"]["Functions"]["create_chat"]["Args"]["p_meta"],
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags([{ type: "Resources" }]));
        } catch {
          // Ignore errors
        }
      },
    }),

    getChat: build.query<Chat, { chatId: string }>({
      queryFn: async ({ chatId }) => {
        const { data, error } = await supabase
          .from("user_full_chats")
          .select("*")
          .eq("id", chatId)
          .single();

        if (error) return { error: serializeError(error) };

        const chat = data as Chat;

        return { data: chat };
      },
      providesTags: (_, __, { chatId }) => [{ type: "Chat", id: chatId }],
    }),

    generateTitle: build.mutation<string, { content: string }>({
      queryFn: async ({ content }) => {
        const { data, error } = await supabase.functions.invoke<{ title: string }>(
          "generate-title",
          {
            body: { content },
          },
        );

        if (error || !data) {
          return { error: serializeError(error) };
        }

        return { data: data.title };
      },
    }),

    updateChat: build.mutation<
      boolean,
      { chatId: string; messages?: ChatMessage[]; meta?: ChatParameters }
    >({
      queryFn: async ({ chatId, messages, meta }) => {
        const { data, error } = await supabase.rpc("update_chat", {
          p_chat_id: chatId,
          p_messages:
            messages as Database["public"]["Functions"]["update_chat"]["Args"]["p_messages"],
          p_meta: meta as Database["public"]["Functions"]["update_chat"]["Args"]["p_meta"],
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async ({ chatId, messages, meta }, { dispatch, queryFulfilled }) => {
        // Optimistic update
        const patchResult = dispatch(
          chatsApi.util.updateQueryData("getChat", { chatId }, (draft) => {
            if (messages) {
              // @ts-ignore (2589): Type instantiation is excessively deep and possibly infinite.ts
              draft.messages = [...messages];
            }

            if (meta) {
              draft.meta = {
                ...(draft.meta && typeof draft.meta === "object" ? draft.meta : {}),
                ...meta,
              };
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useCreateChatMutation,
  useGetChatQuery,
  useGenerateTitleMutation,
  useUpdateChatMutation,
} = chatsApi;

export { chatsApi };
